import React from "react";
import styles from "./Breadcrumbs.module.scss";
import RightArrow from "../../icons/arrow-right.svg";
import classNames from "classnames";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import DefineIcon from "../../icons/define.svg";
import MeasureIcon from "../../icons/measure.svg";
import AnalyseIcon from "../../icons/analyse.svg";
import ImproveIcon from "../../icons/improve.svg";
import ControlIcon from "../../icons/control.svg";
import { useTranslation } from "react-i18next";

const getDataFromPath = (pathname) => {
  if (pathname.includes("/audits")) {
    return { title: "breadcrumbs.title.audits", category: "measure" };
  }
  if (pathname.includes("/findings")) {
    return { title: "breadcrumbs.title.findings", category: "measure" };
  }
  if (pathname.includes("/improvement-projects")) {
    return { title: "breadcrumbs.title.improvementProjects", category: "improve" };
  }
  if (pathname.includes("/archived")) {
    return { title: "breadcrumbs.title.archived", category: "general" };
  }
  return { title: "breadcrumbs.page", category: "general" };
};

const buildBreadcrumbTitleKey = (title = "") => {
  if (title.startsWith("breadcrumbs.")) {
    return title;
  }

  const camelCasedTitle = title
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .map((word, index) => {
      if (!word) return "";

      const lowerCased = word.toLowerCase();

      if (index === 0) {
        return lowerCased;
      }

      return lowerCased.charAt(0).toUpperCase() + lowerCased.slice(1);
    })
    .join("");

  return camelCasedTitle ? `breadcrumbs.title.${camelCasedTitle}` : "breadcrumbs.page";
};

const Breadcrumbs = ({ data }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId } = useParams();

  const { meModeActive } = useSelector((state) => state.filters);
  const derivedData = getDataFromPath(location.pathname);
  const breadcrumbData = data || derivedData;

  const currentUser = useSelector((state) => state.user.email);

  const showMeModeIndicator = [
    "/findings",
    "/improvement-projects",
    "/audits",
    "/risk-analysis",
  ].some((path) => location.pathname.includes(path));

  const getBadgeClassName = (category) => {
    if (!category) return styles.badgeGeneral;

    switch (category.toLowerCase()) {
      case "define":
        return styles.badgeDefine;
      case "analyse":
        return styles.badgeAnalyse;
      case "measure":
        return styles.badgeMeasure;
      case "improve":
        return styles.badgeImprove;
      case "control":
        return styles.badgeControl;
      default:
        return styles.badgeGeneral;
    }
  };

  const getCategoryIcon = (category) => {
    if (!category) return null;

    switch (category.toLowerCase()) {
      case "define":
        return DefineIcon;
      case "measure":
        return MeasureIcon;
      case "analyse":
        return AnalyseIcon;
      case "improve":
        return ImproveIcon;
      case "control":
        return ControlIcon;
      default:
        return null;
    }
  };

  const titleKey = buildBreadcrumbTitleKey(breadcrumbData?.title);

  return (
    <div className={styles.wrapper}>
      <div className={styles.breadcrumbs}>
        <div className={styles.home} onClick={() => navigate(`/${orgId}`)}>
          {t("breadcrumbs.home")}
        </div>
        <img src={RightArrow} alt="Arrow icon" className={styles.icon} />
        <div
          className={classNames(
            styles.badge,
            getBadgeClassName(breadcrumbData?.category?.toLowerCase()),
          )}
        >
          {getCategoryIcon(breadcrumbData?.category) && (
            <img
              src={getCategoryIcon(breadcrumbData.category)}
              alt={`${breadcrumbData.category} icon`}
              className={styles.badgeIcon}
            />
          )}
          {t(`breadcrumbs.category.${breadcrumbData?.category?.toLowerCase() || "general"}`)}
        </div>
        <img src={RightArrow} alt="Arrow icon" className={styles.icon} />
        <div className={styles.title}>
          {t(titleKey || "breadcrumbs.page")}
        </div>
      </div>
      <div className={styles.meModeIndicator}>
        {meModeActive && showMeModeIndicator && (
          <span>{t("breadcrumbs.meMode", { user: currentUser })}</span>
        )}
      </div>
    </div>
  );
};

export default Breadcrumbs;
